import {
	HEATMAP_MULTI_COLOR,
	HEATMAP_OK_COLOR,
	HEATMAP_SINGLE_COLOR,
} from '@/utils/spoofingVisibilityHeatmapUtils';
import { BRDCM_EPH_NO_MSM_COLOR, BRDCM_HELD_COLOR } from '@/utils/brdcmHeatmapUtils';
import type { BrdcmStatusHelpItem, BrdcmStatusHelpKind } from '@/utils/types/brdcmStatusHelpTypes';


type BrdcmStatusHelpSource = {
	key: string;
	labelKey: string;
	textKey: string;
	color: string;
};

type Translate = (key: string) => string;


const hashColor = (color: string): string => '#' + color;

const NO_DATA_COLOR = '#cccccc';

const COVER_STATUS_HELP = [
	{
		key: 'noData',
		labelKey: 'stats.brdcm.legend.noData',
		textKey: 'stats.brdcm.coverHeatmap.statusHelp.noData',
		color: NO_DATA_COLOR,
	},
	{
		key: 'quorum',
		labelKey: 'stats.brdcm.legend.quorum',
		textKey: 'stats.brdcm.coverHeatmap.statusHelp.quorum',
		color: hashColor(HEATMAP_OK_COLOR),
	},
	{
		key: 'held',
		labelKey: 'stats.brdcm.legend.held',
		textKey: 'stats.brdcm.coverHeatmap.statusHelp.held',
		color: hashColor(BRDCM_HELD_COLOR),
	},
	{
		key: 'weak',
		labelKey: 'stats.brdcm.legend.weak',
		textKey: 'stats.brdcm.coverHeatmap.statusHelp.weak',
		color: hashColor(HEATMAP_SINGLE_COLOR),
	},
] as const satisfies readonly BrdcmStatusHelpSource[];

const MSM_STATUS_HELP = [
	{
		key: 'noData',
		labelKey: 'stats.brdcm.legend.noData',
		textKey: 'stats.brdcm.msmHeatmap.statusHelp.noData',
		color: NO_DATA_COLOR,
	},
	{
		key: 'quorum',
		labelKey: 'stats.brdcm.legend.quorum',
		textKey: 'stats.brdcm.msmHeatmap.statusHelp.quorum',
		color: hashColor(HEATMAP_OK_COLOR),
	},
	{
		key: 'held',
		labelKey: 'stats.brdcm.legend.held',
		textKey: 'stats.brdcm.msmHeatmap.statusHelp.held',
		color: hashColor(BRDCM_HELD_COLOR),
	},
	{
		key: 'weak',
		labelKey: 'stats.brdcm.legend.weak',
		textKey: 'stats.brdcm.msmHeatmap.statusHelp.weak',
		color: hashColor(HEATMAP_SINGLE_COLOR),
	},
	{
		key: 'ephNoMsm',
		labelKey: 'stats.brdcm.legend.ephNoMsm',
		textKey: 'stats.brdcm.msmHeatmap.statusHelp.ephNoMsm',
		color: hashColor(BRDCM_EPH_NO_MSM_COLOR),
	},
	{
		key: 'msmNoEph',
		labelKey: 'stats.brdcm.legend.msmNoEph',
		textKey: 'stats.brdcm.msmHeatmap.statusHelp.msmNoEph',
		color: hashColor(HEATMAP_MULTI_COLOR),
	},
] as const satisfies readonly BrdcmStatusHelpSource[];

const STATUS_HELP_BY_KIND = {
	cover: COVER_STATUS_HELP,
	msm: MSM_STATUS_HELP,
} as const satisfies Record<BrdcmStatusHelpKind, readonly BrdcmStatusHelpSource[]>;


export const buildBrdcmStatusHelp = (
	kind: BrdcmStatusHelpKind,
	translate: Translate,
): BrdcmStatusHelpItem[] => {
	return STATUS_HELP_BY_KIND[kind].map(item => ({
		key: item.key,
		label: translate(item.labelKey),
		text: translate(item.textKey),
		color: item.color,
	}));
};
